/**
 * Local Storage Utilities
 * Safe wrapper around localStorage with error handling and type support
 *
 * @module utils/storage
 */

import { STORAGE_KEYS } from "./constants";

/**
 * Check if localStorage is available
 * @returns {boolean} True if localStorage is available
 */
const isLocalStorageAvailable = () => {
  try {
    const test = "__localStorage_test__";
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * Get item from localStorage
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value if key doesn't exist
 * @returns {any} Parsed value or default
 */
export const getItem = (key, defaultValue = null) => {
  if (!isLocalStorageAvailable()) {
    console.warn("localStorage is not available");
    return defaultValue;
  }

  try {
    const item = localStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }

    // Try to parse JSON, fallback to raw value
    try {
      return JSON.parse(item);
    } catch {
      return item;
    }
  } catch (error) {
    console.error(`Error reading from localStorage (${key}):`, error);
    return defaultValue;
  }
};

/**
 * Set item in localStorage
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 * @returns {boolean} True if successful
 */
export const setItem = (key, value) => {
  if (!isLocalStorageAvailable()) {
    console.warn("localStorage is not available");
    return false;
  }

  try {
    const serialized =
      typeof value === "string" ? value : JSON.stringify(value);
    localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    console.error(`Error writing to localStorage (${key}):`, error);
    return false;
  }
};

/**
 * Remove item from localStorage
 * @param {string} key - Storage key
 * @returns {boolean} True if successful
 */
export const removeItem = (key) => {
  if (!isLocalStorageAvailable()) {
    console.warn("localStorage is not available");
    return false;
  }

  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing from localStorage (${key}):`, error);
    return false;
  }
};

/**
 * Clear all items from localStorage
 * @returns {boolean} True if successful
 */
export const clear = () => {
  if (!isLocalStorageAvailable()) {
    console.warn("localStorage is not available");
    return false;
  }

  try {
    localStorage.clear();
    return true;
  } catch (error) {
    console.error("Error clearing localStorage:", error);
    return false;
  }
};

/**
 * Get all keys from localStorage
 * @returns {string[]} Array of keys
 */
export const getAllKeys = () => {
  if (!isLocalStorageAvailable()) {
    return [];
  }

  try {
    return Object.keys(localStorage);
  } catch (error) {
    console.error("Error getting localStorage keys:", error);
    return [];
  }
};

/**
 * Check if key exists in localStorage
 * @param {string} key - Storage key
 * @returns {boolean} True if key exists
 */
export const hasItem = (key) => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    return localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
};

/**
 * Get multiple items at once
 * @param {string[]} keys - Array of storage keys
 * @returns {Object} Object with key-value pairs
 */
export const getItems = (keys) => {
  const result = {};
  keys.forEach((key) => {
    result[key] = getItem(key);
  });
  return result;
};

/**
 * Set multiple items at once
 * @param {Object} items - Object with key-value pairs
 * @returns {boolean} True if all successful
 */
export const setItems = (items) => {
  let allSuccessful = true;
  Object.entries(items).forEach(([key, value]) => {
    if (!setItem(key, value)) {
      allSuccessful = false;
    }
  });
  return allSuccessful;
};

/**
 * Remove multiple items at once
 * @param {string[]} keys - Array of storage keys
 * @returns {boolean} True if all successful
 */
export const removeItems = (keys) => {
  let allSuccessful = true;
  keys.forEach((key) => {
    if (!removeItem(key)) {
      allSuccessful = false;
    }
  });
  return allSuccessful;
};

// Auth-specific helpers
export const auth = {
  getToken: () => getItem(STORAGE_KEYS.TOKEN),
  setToken: (token) => setItem(STORAGE_KEYS.TOKEN, token),
  removeToken: () => removeItem(STORAGE_KEYS.TOKEN),

  getUser: () => getItem(STORAGE_KEYS.USER),
  setUser: (user) => setItem(STORAGE_KEYS.USER, user),
  removeUser: () => removeItem(STORAGE_KEYS.USER),

  clearAuth: () => removeItems([STORAGE_KEYS.TOKEN, STORAGE_KEYS.USER]),
};

// Theme-specific helpers
export const theme = {
  getMode: () => getItem(STORAGE_KEYS.THEME_MODE, "dark"),
  setMode: (mode) => setItem(STORAGE_KEYS.THEME_MODE, mode),
};

// Preferences-specific helpers
export const preferences = {
  get: () => getItem(STORAGE_KEYS.USER_PREFERENCES, {}),
  set: (prefs) => setItem(STORAGE_KEYS.USER_PREFERENCES, prefs),
  update: (updates) => {
    const current = preferences.get();
    return setItem(STORAGE_KEYS.USER_PREFERENCES, { ...current, ...updates });
  },
};

export default {
  getItem,
  setItem,
  removeItem,
  clear,
  getAllKeys,
  hasItem,
  getItems,
  setItems,
  removeItems,
  auth,
  theme,
  preferences,
  isAvailable: isLocalStorageAvailable,
};

